import { useCallback, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { App } from './App'
import { Button } from './components/common'
import { WorkspaceMentions } from './components/Mentions'
import { SignIn } from './components/SignIn'
import { AppSkeleton } from './components/Skeleton'
import { api, ApiError, setWorkspace, type Bootstrap, type Me, type PublicInfo } from './lib/api'
import { goAs, hopTo, reachableWorkspaces, superadminHop, type Reachable } from './lib/identities'
import { StoreProvider } from './lib/store'
import { applyBrand, initTheme } from './lib/theme'
import { AdminPage } from './routes/AdminPage'
import { ClientSeat } from './routes/ClientSeat'
import { PwaCheck } from './routes/PwaCheck'

import './global.css'

/**
 * One deployment, many workspaces, and the URL says which.
 *
 *   /w/<slug>/...        a team's workspace. The app, mounted under that prefix.
 *   /r/<slug>/<token>    a client's page in that workspace. Never mounts the store.
 *   /admin               the superadmin surface. On no roster; its own door.
 *   /home                where `/` sends a signed-in person who has not been
 *                        anywhere yet: pick a workspace, or say which one.
 *
 * `/` itself never reaches this file signed out: the Worker serves the landing
 * page. Signed in, it redirects to the last workspace, or here.
 */
const path = location.pathname
const clientMatch = /^\/r\/([^/]+)\/([^/]+)/.exec(path)
const workspaceMatch = /^\/w\/([^/]+)/.exec(path)

function Boot({ slug }: { slug: string }) {
  const [boot, setBoot] = useState<Bootstrap | null>(null)
  const [info, setInfo] = useState<PublicInfo | null>(null)
  const [hop, setHop] = useState<Reachable | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const b = await api.bootstrap()
      applyBrand(b.brand)
      setBoot(b)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Signed out, or signed in as an address with no seat HERE. Those are not
        // the same thing, and the door used to treat them as one: it asked for an
        // email and mailed a link even when another identity on this device held a
        // seat in this very workspace. Ask who this device is before drawing it.
        api
          .me()
          .then((m) => setHop(hopTo(m, slug)))
          .catch(() => undefined)
        // Fetch the public brand so the sign-in screen is theirs.
        try {
          const i = await api.info()
          applyBrand(i.brand)
          setInfo(i)
        } catch (e) {
          setError(
            e instanceof ApiError && e.status === 404
              ? 'There is no workspace at this address.'
              : 'Could not reach the server.',
          )
        }
        return
      }
      if (err instanceof ApiError && err.status === 404) {
        setError('There is no workspace at this address.')
        return
      }
      setError(err instanceof Error ? err.message : 'Could not reach the server.')
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <Problem text={error} retry={() => void load()} />

  if (boot) {
    return (
      <BrowserRouter basename={`/w/${slug}`}>
        <StoreProvider boot={boot}>
          <WorkspaceMentions>
            <App />
          </WorkspaceMentions>
        </StoreProvider>
      </BrowserRouter>
    )
  }

  if (info) return <SignIn info={info} hop={hop} onSignedIn={() => void load()} />

  return <AppSkeleton />
}

/**
 * Signed in, no workspace in the URL. Almost everyone has exactly one seat and
 * goes straight there; the switcher in the app covers the rest. This page exists
 * for the moments in between: a first sign-in from `/admin`, a suspended
 * workspace, an address on no roster yet.
 *
 * **Every identity's seats, not the active one's.** Somebody signed in under two
 * addresses has two sets of workspaces and one device; listing half of them made
 * the other half look lost. Opening one becomes that identity on the way in.
 */
function Home() {
  const [me, setMe] = useState<Me | null>(null)
  const [signedOut, setSignedOut] = useState(false)
  const [slug, setSlug] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .me()
      .then((m) => {
        const all = reachableWorkspaces(m)
        if (all.length === 1 && !superadminHop(m)) {
          const only = all[0]!
          void goAs(only.token, `/w/${only.slug}`)
          return
        }
        setMe(m)
      })
      .catch(() => setSignedOut(true))
  }, [])

  const reachable = reachableWorkspaces(me)
  const admin = superadminHop(me)

  const field = {
    borderColor: 'color-mix(in srgb, var(--fg) 14%, transparent)',
    background: 'var(--panel)',
    color: 'var(--fg)',
  }

  return (
    <div
      className="flex min-h-dvh items-center justify-center px-6"
      style={{ background: 'var(--bg)', color: 'var(--fg)' }}
    >
      <div className="w-full max-w-[24rem]">
        <h1 className="text-title mb-4 font-semibold tracking-tight">crum</h1>
        {reachable.length > 0 ? (
          <div className="mb-5">
            <p className="meta mb-2">Your workspaces</p>
            <ul className="flex flex-col gap-1.5">
              {reachable.map((s) => (
                <li key={s.slug}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true)
                      void goAs(s.token, `/w/${s.slug}`)
                    }}
                    className="rounded-control text-body block w-full border px-3 py-2 text-left font-medium disabled:opacity-50"
                    style={field}
                  >
                    {s.name}
                    {/* Named, because opening it changes who you are signed in as
                        and that should not be a surprise. */}
                    {s.via ? <span className="meta block">as {s.via}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {admin ? (
          <p className="text-body mb-5">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true)
                void goAs(admin.token, '/admin')
              }}
              className="underline underline-offset-2 disabled:opacity-50"
            >
              Manage workspaces
            </button>
            {admin.token ? <span className="meta"> · as {admin.email}</span> : null}
          </p>
        ) : null}
        {signedOut ? <SignInByEmail field={field} /> : null}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const s = slug.trim().toLowerCase()
            if (s) location.assign(`/w/${encodeURIComponent(s)}`)
          }}
          className="flex flex-col gap-2"
        >
          <label className="meta">
            {signedOut ? 'Or open a workspace by its address' : 'Go to a workspace'}
          </label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="workspace"
            className="rounded-control text-body border px-3 py-2 outline-none"
            style={field}
          />
          <Button type="submit" variant={signedOut ? 'default' : 'primary'} disabled={!slug.trim()}>
            Continue
          </Button>
        </form>
        {signedOut ? (
          <p className="note mt-4">
            Your team gave you the address of its workspace. It looks like{' '}
            <code className="num">crum.saltbark.com/w/yourteam</code>.
          </p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Signing in with an address rather than a workspace.
 *
 * What the landing page was missing: somebody who has an account has an EMAIL, not a
 * slug, and every door here was a per-workspace one you had to know the address of.
 * One letter, and then this same screen lists everywhere the address reaches — the
 * list is already here, because a session is an identity and the seats hang off the
 * identity rather than off the door you came through.
 *
 * Whether the address can reach anything is not something the server will say, so
 * this says the same thing whatever was typed.
 */
function SignInByEmail({ field }: { field: React.CSSProperties }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (sent) {
    return (
      <form
        className="mb-6 flex flex-col gap-2"
        onSubmit={async (e) => {
          e.preventDefault()
          setBusy(true)
          setError('')
          try {
            await api.submitCode(email.trim(), code.trim())
            location.assign('/home')
          } catch {
            setError('That code did not work. Check the newest email, or ask for another.')
            setBusy(false)
          }
        }}
      >
        <p className="text-body leading-relaxed">
          If <b>{email.trim()}</b> can reach a workspace here, a link is on its way. Tap the
          button in it, or type the code.
        </p>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          autoFocus
          className="num rounded-control border px-3 py-2 text-center text-[19px] outline-none"
          style={{ ...field, letterSpacing: '0.3em' }}
        />
        <Button type="submit" variant="primary" disabled={busy || code.length < 6}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        {error ? (
          <p className="note" style={{ color: 'var(--crit)' }}>
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setSent(false)
            setCode('')
            setError('')
          }}
          className="meta self-start underline underline-offset-2"
        >
          use a different address
        </button>
      </form>
    )
  }

  return (
    <form
      className="mb-6 flex flex-col gap-2"
      onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true)
        try {
          await api.signIn(email.trim())
          setSent(true)
        } finally {
          setBusy(false)
        }
      }}
    >
      <label className="meta" htmlFor="signin-email">
        Sign in with your email
      </label>
      <input
        id="signin-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@yourteam.com"
        autoComplete="email"
        autoFocus
        className="rounded-control text-body border px-3 py-2 outline-none"
        style={field}
      />
      <Button type="submit" variant="primary" disabled={busy || !email.includes('@')}>
        {busy ? 'Sending…' : 'Email me a sign-in link'}
      </Button>
      <p className="note">
        You will land on the workspaces this address can open. There is no password: the
        email carries a link and a code, and either one opens it.
      </p>
    </form>
  )
}

function Problem({ text, retry }: { text: string; retry: () => void }) {
  return (
    <div
      className="text-body flex min-h-dvh items-center justify-center px-6 text-center"
      style={{ background: 'var(--bg)', color: 'var(--muted)' }}
    >
      <div>
        <p>{text}</p>
        <span className="mt-3 inline-flex">
          <Button onClick={retry}>Try again</Button>
        </span>
        <p className="mt-3">
          <a href="/home" className="underline underline-offset-2">
            Go somewhere else
          </a>
        </p>
      </div>
    </div>
  )
}

initTheme()

let root: React.ReactNode
if (clientMatch) {
  setWorkspace(decodeURIComponent(clientMatch[1]!))
  root = (
    <BrowserRouter>
      <Routes>
        <Route path="/r/:slug/:token" element={<ClientSeat />} />
      </Routes>
    </BrowserRouter>
  )
} else if (path.startsWith('/pwa-check')) {
  // Temporary: a diagnostic for the installed app's top bar. See routes/PwaCheck.tsx.
  root = <PwaCheck />
} else if (path.startsWith('/admin')) {
  root = <AdminPage />
} else if (workspaceMatch) {
  const slug = decodeURIComponent(workspaceMatch[1]!)
  setWorkspace(slug)
  root = <Boot slug={slug} />
} else {
  root = <Home />
}

createRoot(document.getElementById('root')!).render(root)
